// ============================================================
// BattlefieldScene - the SVG the tactical map is drawn into.
//
// Deliberately dumb: it takes a depth-sorted face list from
// `engine/battlefield/scene` and maps it to <polygon> elements. It makes
// no decisions about order, visibility or brightness - those are pure
// functions with their own tests, and keeping them out of here is what
// makes the map testable at all.
//
// The viewBox is FIXED. The projection therefore always works in the
// same 1000×700 space, and the browser scales that to whatever the panel
// is; no ResizeObserver, no layout dependency, and the same numbers in a
// unit test as in a browser.
// ============================================================
import { memo, useMemo } from 'react';
import { buildScene } from '../engine/battlefield/scene';
import type { Boundary } from '../engine/battlefield/boundary';
import type { VoxelGrid } from '../engine/battlefield/voxels';
import { SCENE_VIEW } from '../engine/battlefield/projection';
import type { Camera } from '../engine/battlefield/projection';
import type { Solid } from '../engine/battlefield/types';
import type { TerrainAnimation } from '../engine/battlefield/terrainAnimation';
import styles from '../styles/Battlefield.module.css';
import { SceneFloor } from './battlefieldScene/SceneFloor';
import { SceneDefs, SceneFaces, TerrainMarks } from './battlefieldScene/SceneFaces';

/** What either renderer takes (DisplayScene uses this type too, so the two cannot drift apart). */
export interface BattlefieldSceneProps extends React.SVGProps<SVGSVGElement> {
  boundary: Boundary;
  solids: Solid[];
  camera: Camera;
  /** GM view: include terrain the players cannot see. */
  includeHidden?: boolean;
  /** Faint metre grid on the ground. */
  showGrid?: boolean;
  /** Outlined in Build mode, so Delete has an obvious subject. */
  selectedSolidId?: string | null;
  /** Holes are punched in the ground for whatever has been dug out of it. */
  voxels?: VoxelGrid | null;
  /**
   * Hatch dangerous and impassable ground. Off by default and per viewer:
   * a GM who painted the terrain has already said what it looks like.
   */
  showTerrainMarks?: boolean;
  /**
   * Each painted surface's name on the ground (the display only). On by default and per viewer: a map full of names
   * distracts some players (2026-11-04).
   */
  showSurfaceNames?: boolean;
  /**
   * Which arrival the terrain uses as it appears. Per viewer, and
   * `'none'` by default - see `TERRAIN_ANIMATIONS`.
   */
  animation?: TerrainAnimation;
  /**
   * When the stage was last cut (engine/scenes `StageDoc.cutAt`): the display folds the set and raises the next
   * (plan map-display, I). The classic map swaps in place, as it always has.
   */
  cutAt?: number | null;
  /** When the stage was last struck: the display flashes the set's outline (plan map-display, I). */
  struckAt?: number | null;
  /** Under a lowered curtain, for this viewer (the stage's `curtainHides`): the display waits to play a cut. */
  curtained?: boolean;
  /** Sets the cursor: a hand for the camera, a crosshair for precision. */
  children?: React.ReactNode;
}

function BattlefieldSceneImpl({
  boundary,
  solids,
  camera,
  includeHidden = false,
  showGrid = true,
  selectedSolidId = null,
  voxels = null,
  showTerrainMarks = false,
  animation = 'none',
  // The display's cut (plan map-display, I); the classic map swaps in place. Taken so it stays off the <svg>.
  cutAt: _cutAt,
  struckAt: _struckAt,
  curtained: _curtained,
  showSurfaceNames: _names,
  children,
  ...svgProps
}: BattlefieldSceneProps) {
  const faces = useMemo(
    () => buildScene(solids, camera, SCENE_VIEW, { includeHidden }),
    [solids, camera, includeHidden],
  );
  // The CURSOR is driven by the live gesture, not by the mode -
  // `data-measuring` and `data-moving`, set by the panel. A mode is not a
  // thing you are doing; a drag is. The floor makes the map read as a
  // place rather than objects floating in the void.
  return (
    <svg
      className={styles.scene}
      viewBox={`0 0 ${SCENE_VIEW.width} ${SCENE_VIEW.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Tactical battlefield"
      data-face-count={faces.length}
      {...svgProps}
    >
      <SceneDefs />
      <SceneFloor
        boundary={boundary}
        camera={camera}
        voxels={voxels}
        showGrid={showGrid}
        classes={{ ground: styles.ground, grid: styles.grid, boundary: styles.boundary }}
      />
      <g data-testid="battlefield-solids">
        <SceneFaces faces={faces} selectedSolidId={selectedSolidId} animation={animation} />
        {showTerrainMarks && <TerrainMarks faces={faces} />}
      </g>
      {children}
    </svg>
  );
}

export default memo(BattlefieldSceneImpl);
